import { useCallback, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import NavBarButton from 'components/NavKit/NavBarButton/NavBarButton'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineArrowToBottomIcon from 'LuxKit/Icons/line/LineArrowToBottomIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useTripChatItineraryExport } from '../hooks/useTripChatItineraryExport'

const PanelContent = styled.div`
  position: relative;
  display: flex;
  width: ${rem(435)};
  max-width: calc(100vw - ${rem(32)});
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-height: ${rem(48)};
  padding: ${rem(16)} ${rem(16)} ${rem(8)};
  gap: ${rem(16)};
`

const PanelTitle = styled(Heading)`
  flex: 1 1 auto;
  color: ${props => props.theme.palette.neutral.surface.darkForeground};
`

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(16)};
  width: ${rem(24)};
  height: ${rem(24)};
  padding-block: 0 ${rem(4)};
  padding-inline: ${rem(4)} 0;
  color: ${props => props.theme.palette.neutral.surface.darkForeground};

  > svg:first-of-type {
    width: ${rem(20)};
    height: ${rem(20)};
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus,
  &:not(:disabled):active {
    color: ${props => props.theme.palette.neutral.surface.darkForeground};
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &:not(:disabled):active {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const ExportRows = styled.div`
  display: grid;
  gap: 0;
  width: 100%;
  padding-bottom: ${rem(16)};
`

const ExportRow = styled.button`
  display: grid;
  grid-template-columns: ${rem(92)} minmax(0, 1fr) ${rem(40)};
  gap: ${rem(12)};
  align-items: center;
  width: 100%;
  height: ${rem(92)};
  padding: ${rem(12)} ${rem(16)};
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.darkForeground};
  cursor: pointer;
  text-align: start;

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &:disabled {
    cursor: default;
  }
`

const ThumbnailFrame = styled.div`
  width: ${rem(92)};
  align-self: stretch;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const ExportCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;
  height: ${rem(68)};
`

const ExportTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(24)};
`

const ExtensionBadge = styled(BodyText).attrs({ variant: 'small', weight: 'bold' })`
  display: inline-flex;
  align-items: center;
  height: ${rem(24)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.XS};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.darkForeground};
  line-height: 16px;
`

const DownloadGlyph = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: ${rem(40)};
  height: ${rem(40)};
  align-self: center;

  > svg:first-of-type {
    width: ${rem(20)};
    height: ${rem(20)};
  }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 800ms linear infinite;
`

interface Props {
  chatId: number | undefined
  disabled?: boolean
  className?: string
}

export default function DownloadItineraryButton({ chatId, disabled = false, className }: Props) {
  const intl = useIntl()
  const dropdownId = useId()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const buttonLabel = intl.formatMessage({
    id: 'XAgOeZ7Gbt5mGCuvency',
    defaultMessage: 'Download itinerary',
    description: 'Accessible label for the trip planner itinerary download menu button',
  })

  const triggerLabel = intl.formatMessage({
    id: 'RnvgLoKWESfvlRTP1S7a',
    defaultMessage: 'Download',
    description: 'Button text for opening trip planner itinerary export options',
  })

  const panelTitle = intl.formatMessage({
    id: 'EIcyhNtIQDUyEAw--cKU',
    defaultMessage: 'Export itinerary',
    description: 'Title for the trip planner itinerary export options menu',
  })

  const spreadsheetTitle = intl.formatMessage({
    id: '-Vv_YVoSjoMzSkN3nCfP',
    defaultMessage: 'Spreadsheet',
    description: 'Title for the trip planner spreadsheet itinerary download option',
  })

  const spreadsheetSubtitle = intl.formatMessage({
    id: 'wbOUU-VtrEOspedI7EK6',
    defaultMessage: 'Day-by-day rows with date, activity and location.',
    description: 'Subtitle for the trip planner spreadsheet itinerary download option',
  })

  const spreadsheetExtension = intl.formatMessage({
    id: 'B-tElaTxZpuZFIgU_b10',
    defaultMessage: '.xlsx',
    description: 'File extension label for the trip planner spreadsheet itinerary download option',
  })

  const pdfTitle = intl.formatMessage({
    id: 'KiuoXKv1ExBBmRmEKSiJ',
    defaultMessage: 'PDF',
    description: 'Title for the trip planner PDF itinerary download option',
  })

  const pdfSubtitle = intl.formatMessage({
    id: 'trUswUCjs9n4H5DT5IcJ',
    defaultMessage: 'Print or share a clean, read-only version.',
    description: 'Subtitle for the trip planner PDF itinerary download option',
  })

  const pdfExtension = intl.formatMessage({
    id: 'nAMxr_841j8wnwOdDYB9',
    defaultMessage: '.pdf',
    description: 'File extension label for the trip planner PDF itinerary download option',
  })

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  const {
    downloadPdf,
    downloadSpreadsheet,
    isExportLoading,
    loadingFormat,
  } = useTripChatItineraryExport({
    chatId,
    onExportCreated: closeDropdown,
  })

  const toggleDropdown = useCallback(() => {
    setIsOpen(previous => !previous)
  }, [])

  return (
    <>
      <NavBarButton
        className={className}
        icon={<>
          {isExportLoading && <SpinnerIcon />}
          {!isExportLoading && <LineArrowToBottomIcon />}
        </>}
        aria-label={buttonLabel}
        aria-controls={isOpen ? dropdownId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        disabled={disabled || !chatId || isExportLoading}
        onClick={toggleDropdown}
        ref={triggerRef}
      >
        {triggerLabel}
      </NavBarButton>
      <DropdownSheet
        id={dropdownId}
        size="content"
        surface="elevated"
        autoHeight
        open={isOpen}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        placement="bottom-end"
        portaled
        noPaddingOnBody
      >
        <PanelContent>
          <PanelHeader>
            <PanelTitle variant="heading6">{panelTitle}</PanelTitle>
            <CloseButton
              kind="tertiary"
              variant="ghost"
              size="xsmall"
              shape="square"
              aria-label={intl.formatMessage({
                id: '3b4oCQAsMvXbusBRGSzl',
                defaultMessage: 'Close export menu',
                description: 'Accessible label for closing the trip planner itinerary export menu',
              })}
              onClick={closeDropdown}
            >
              <LineTimesIcon />
            </CloseButton>
          </PanelHeader>

          <ExportRows>
            <ExportRow
              type="button"
              disabled={isExportLoading}
              onClick={downloadPdf}
            >
              <ThumbnailFrame>
                <Image id="pdf-thumbnail" width={92} height={68} fit="center" objectFit="cover" alt="" />
              </ThumbnailFrame>
              <ExportCopy>
                <ExportTitle>
                  <BodyText variant="medium" weight="bold">{pdfTitle}</BodyText>
                  <ExtensionBadge variant="small" weight="bold">{pdfExtension}</ExtensionBadge>
                </ExportTitle>
                <BodyText variant="medium" colour="neutral-one">{pdfSubtitle}</BodyText>
              </ExportCopy>
              <DownloadGlyph aria-hidden="true">
                {loadingFormat === 'pdf' && <SpinnerIcon />}
                {loadingFormat !== 'pdf' && <LineArrowToBottomIcon />}
              </DownloadGlyph>
            </ExportRow>

            <ExportRow
              type="button"
              disabled={isExportLoading}
              onClick={downloadSpreadsheet}
            >
              <ThumbnailFrame>
                <Image id="excel-thumbnail" width={92} height={68} fit="center" objectFit="cover" alt="" />
              </ThumbnailFrame>
              <ExportCopy>
                <ExportTitle>
                  <BodyText variant="medium" weight="bold">{spreadsheetTitle}</BodyText>
                  <ExtensionBadge variant="small" weight="bold">{spreadsheetExtension}</ExtensionBadge>
                </ExportTitle>
                <BodyText variant="medium" colour="neutral-two">{spreadsheetSubtitle}</BodyText>
              </ExportCopy>
              <DownloadGlyph aria-hidden="true">
                {loadingFormat === 'xlsx' && <SpinnerIcon />}
                {loadingFormat !== 'xlsx' && <LineArrowToBottomIcon />}
              </DownloadGlyph>
            </ExportRow>
          </ExportRows>
        </PanelContent>
      </DropdownSheet>
    </>
  )
}
